import posthog from "posthog-js";
import axios from "axios";

const localFeatureFlags =
  process.env.NODE_ENV === "development"
    ? (process.env.VUE_APP_LOCAL_FEATURE_FLAGS || "")
        .split(",")
        .map(flag => flag.trim())
        .filter(Boolean)
    : [];

export const isFeatureFlagEnabled = name => {
  if (localFeatureFlags.includes(name)) return true;
  posthog.reloadFeatureFlags();
  return posthog.isFeatureEnabled(name);
};

export const isFeatureFlagEnabledAsync = name => {
  if (localFeatureFlags.includes(name)) return Promise.resolve(true);
  return new Promise(resolve => {
    let reloadRequested = false;
    let settled = false;
    let unsubscribe;
    const finish = enabled => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      if (typeof unsubscribe === "function") unsubscribe();
      resolve(enabled);
    };
    const timeoutId = setTimeout(() => finish(false), 5000);

    try {
      unsubscribe = posthog.onFeatureFlags(() => {
        if (reloadRequested) {
          finish(Boolean(posthog.isFeatureEnabled(name)));
        }
      });
      reloadRequested = true;
      posthog.reloadFeatureFlags();
    } catch {
      finish(false);
    }
  });
};

export const isFeatureFlagEnabledForUserAsync = async (name, userId) => {
  if (localFeatureFlags.includes(name)) return true;
  try {
    const { token, api_host: host } = posthog.config || {};
    if (!token || !host || userId == null) return false;
    // SDK 1.9 broadcasts flag responses without their original identity.
    // Keep this response tied to the requested user rather than shared callbacks.
    const body = new URLSearchParams({
      data: btoa(JSON.stringify({ token, distinct_id: String(userId) }))
    });
    const { data } = await axios.post(`${host.replace(/\/$/, "")}/decide/`, body, {
      timeout: 5000,
      headers: { "Content-Type": "application/x-www-form-urlencoded" }
    });
    const flags = data && data.featureFlags;
    return Array.isArray(flags) ? flags.includes(name) : Boolean(flags && flags[name] === true);
  } catch {
    return false;
  }
};

export const identify = userId => {
  posthog.identify(userId, {
    name: userId
  });
};

export const FEATURE_FLAG = {
  CEN_846_STRIPE_SUBSCRIPTION_UI: "CEN-846_stripe-subscription-ui",
  SERVICE_UNAVAILABLE: "service-unavailable",
  GET_UNVERIFIED_FACILITIES: "get-unverified-facilities",
  SHOW_REVIEW_BOOSTER: "show-review-booster",
  SHOW_REVIEW_BOOSTER_PROMO: "show-review-booster-promo",
  SHOW_EDIT_FACILITY_FROM_FILE: "show-edit-facility-from-file",
  SHOW_INTEGRATIONS: "show-integrations",
  SHOW_RB_CATEGORIES: "show-rb-categories",
  SHOW_CONVERSION_CHARTS: "show-conversion-charts",
  SHOW_FACEBOOK_INTEGRATION: "show-facebook-integration",
  SHOW_TOMTOM_INTEGRATION: "show-tomtom-integration",
  CEN_847_TWO_FACTOR_AUTHENTICATION: "CEN-847_two-factor-authentication",
  SHOW_RB_CATEGORIES_ANALYTICS: "show-rb-categories-analytics",
  SHOW_REVIEW_ADDED_WITH_RB: "show-review-added-with-rb",
  USE_OPTIMIZED_API_SCHEME: "use-optimized-api-scheme",
  CN_3276_EXTEND_BUSINESS_CARD_FILTERING:
    "CN-3276_extend-business-card-filtering",
  CN_5335_BING_MAPS_LINK: "CN-5335_bing-maps-link",
  SHOW_DASHBOARD_INCREASE_INDICATOR:
    "CN-5587-dashboard-show-increase-indicators",
  CN_5721_SERVICES: "CN-5721_services",
  CN_5810_INTERCOM_INTEGRATION: "CN-5810_intercom-integration",
  CN_5906_FILTER_BING_STATUS: "CN-5906_filter-bing-status",
  SHOW_SERVICE_ITEMS_EXTERNAL_CHANGES: "show-service-items-external-changes",
  SHOW_STORE_LOCATOR_EXTRA_INFO: "show-store-locator-extra-info",
  CN_6145_FACILITIES_QUALITY: "CN-6145_facilities_quality",
  CN_6363_LIMITS_DELETION: "CN-6363_limits_deletion",
  CN_6392_MASS_SERVICES_MANAGING: "CN-6392_mass-services-managing",
  CN_6531_SPANISH_TRANSLATIONS: "CN-6531_spanish-translations",
  CN_6543_QUESTIONS_AND_ANSWERS: "CN-6543_questions-and-answers",
  CN_6573_FAQ_STORE_LOCATOR: "CN-6573_faq-store-locator",
  CN_6637_COPY_FACEBOOK_POSTS: "CN-6637_copy-facebook-posts",
  CN_6665_AUTO_REVIEW_REPLY: "CN-6665_auto-review-reply",
  CN_5526_APPLE_MAPS: "CN-5526_apple-maps",
  CN_6968_BULK_ASSIGN_CATEGORIES_FOR_REPLIED_REVIEWS:
    "CN-6968_bulk-assign-categories-for-replied-reviews",
  CN_7152_TIMEZONE_SETTINGS: "CN-7152_timezone-settings",
  CN_7285_SHOW_FACILITY_COORDINATES_EDIT_FIELDS:
    "CN-7285_show-facility-coordinates-edit-fields",
  SHOW_ADD_FACILITIES_TO_GROUP_FROM_CSV:
    "show-add-facilities-to-group-from-csv",
  CEN_31_QA_READ_ONLY: "CEN-31_qa-read-only",
  APP_STORE_ACCOUNTS_PAGE: "app-store-accounts-page",
  SHOW_APPS_CONFIG_PAGE: "mobile-app-accounts-page",
  CEN_37_COMPETITOR_REVIEW_ANALYSIS: "CEN-37_competitor_review_analysis",
  REVIEW_BOOSTER_STATISTICS: "review-booster-statistics",
  POSTS_CANCELLING: "posts-cancelling",
  CEN_210_APPLE_MAPS_LINK: "CEN-210_apple_maps_link",
  CEN_235_COMPEITION_ANALYSIS_FREE_TOOL:
    "CEN-235_competition-analysis-free-tool",
  CEN_212_SHOW_REVIEW_PHOTOS: "CEN-212_show_review_photos",
  CEN_674_USE_DIRECT_REVIEW_MEDIA: "CEN-674_use-direct-review-media",
  CEN_832_REPORT_GOOGLE_REVIEW: "CEN-832_report-google-review",
  CEN_832_GOOGLE_REVIEW_LINK: "CEN-832_google-review-link",
  CEN_394_KEYWORDS_IMPRESSIONS_ANALYTICS:
    "CEN-394_keywords-impressions-analytics",
  CEN_444_COMPETITORS_COMPARISON_INLINE_ADD:
    "CEN-444_competitors-comparison-inline-add",
  CEN_535_SHOW_REPORT_SELECTION: "show-report-selection",
  CEN_760_SHOW_CUSTOMERS_MEDIA: "CEN-760_show-customers-media",
  CEN_779_SHOW_INSIGHTS_PREDICTION: "CEN-779_show-insights-prediction",
  CEN_845_HIDE_GMB_REVIEWS: "CEN-845_hide-gmb-reviews",
  CEN_888_EDIT_AI_CONTEXT: "CEN-888_edit-ai-context"
};
